import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import type { IdentifyResult, RouterContext } from '@/idv2/routes/router-context';
import { getLogger } from '@/idv2/utils/logger';
import { getProceedQueryKey } from '@/idv2/utils/proceed-query';
import { shouldWaitForComponentsSdk } from '@/idv2/utils/route-helpers';

const { logInfo } = getLogger({ name: 'idv2', location: 'identify' });

/**
 * Invalidates the proceed query and redirects to the onboarding token route.
 * This is used in loaders/beforeLoad when we want to skip the current step
 * and let the token route determine the next requirement.
 */
export const redirectToCheckRequirements = async (queryClient: QueryClient, token: string): Promise<never> => {
  await queryClient.invalidateQueries({
    type: 'all',
    queryKey: getProceedQueryKey(),
    refetchType: 'none',
  });
  throw redirect({
    to: '/onboarding/$token',
    params: { token },
  });
};

/** Hands the identify result to the host app. Loaders and components both finish identify here. */
export const reportIdentifyDone = (onIdentifyDone: RouterContext['onIdentifyDone'], result: IdentifyResult) => {
  logInfo('Identify completed', { engine: 'idv2' });
  onIdentifyDone?.(result);
};

/**
 * Completes the identify flow: calls onIdentifyDone then redirects to check requirements
 * (or to wait-for-components-sdk if components SDK relay is needed).
 *
 * Used in loaders/beforeLoad (non-React context).
 */
export const redirectAfterIdentify = async (
  context: Pick<RouterContext, 'onIdentifyDone' | 'queryClient' | 'componentsSdkRelay'>,
  result: IdentifyResult,
): Promise<never> => {
  const { authToken } = result;
  reportIdentifyDone(context.onIdentifyDone, result);
  if (shouldWaitForComponentsSdk(context.componentsSdkRelay)) {
    throw redirect({ to: '/wait-for-components-sdk/$token', params: { token: authToken } });
  }
  throw await redirectToCheckRequirements(context.queryClient, authToken);
};
